import type {
  EstimateProductPaymentRequest,
  ProductsDataSource,
} from '~/api/data-source/products';
import { productsDataSource } from '~/api/data-source/products';
import type {
  EstimateProductPayment,
  MarketProductDetail,
  MarketSelectProduct,
  ProductOptionList,
  RecentlyViewedProduct,
} from '~/api/model';
import {
  estimateProductPaymentSchema,
  marketProductDetailSchema,
  marketSelectProductSchema,
  parseModel,
  parseModelArray,
  productOptionListSchema,
  recentlyViewedProductSchema,
} from '~/api/model';

export interface ProductsRepository {
  getSimilarProducts(offerId: number): Promise<MarketSelectProduct[]>;
  getCrossSellProducts(offerId: number): Promise<MarketSelectProduct[]>;
  getRecentlyViewedProducts(): Promise<RecentlyViewedProduct[]>;
  getProductDetail(offerId: number): Promise<MarketProductDetail>;
  getProductOptions(offerId: number): Promise<ProductOptionList>;
  estimateProductPayment(
    body: EstimateProductPaymentRequest,
  ): Promise<EstimateProductPayment>;
}

export class ProductsRepositoryImpl implements ProductsRepository {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    private readonly dataSource: ProductsDataSource = productsDataSource,
  ) {}

  async getSimilarProducts(offerId: number) {
    const response = await this.dataSource.getSimilarProducts(offerId);
    return parseModelArray(
      marketSelectProductSchema,
      response,
      'MarketSelectProduct',
    );
  }

  async getCrossSellProducts(offerId: number) {
    const response = await this.dataSource.getCrossSellProducts(offerId);
    return parseModelArray(
      marketSelectProductSchema,
      response,
      'MarketSelectProduct',
    );
  }

  async getRecentlyViewedProducts() {
    const response = await this.dataSource.getRecentlyViewedProducts();
    return parseModelArray(
      recentlyViewedProductSchema,
      response,
      'RecentlyViewedProduct',
    );
  }

  async getProductDetail(offerId: number) {
    const response = await this.dataSource.getProductDetail(offerId);
    return parseModel(
      marketProductDetailSchema,
      response,
      'MarketProductDetail',
    );
  }

  async getProductOptions(offerId: number) {
    const response = await this.dataSource.getProductOptions(offerId);
    return parseModel(productOptionListSchema, response, 'ProductOptionList');
  }

  async estimateProductPayment(body: EstimateProductPaymentRequest) {
    const response = await this.dataSource.estimateProductPayment(body);
    return parseModel(
      estimateProductPaymentSchema,
      response,
      'EstimateProductPayment',
    );
  }
}

export const productsRepository = new ProductsRepositoryImpl();
